const FEED_BOOLEAN_FIELDS = new Set(['twitter', 'website', 'devLeft', 'throughPumpfun', 'showDuplicates'])
const FEED_NUMBER_FIELDS = new Set(['holders', 'minLiquidityLamports', 'maxAgeMs'])
const TRADE_NUMBER_LIMITS = { pct: [1, 100], buyLamports: [0, 1_000_000_000_000], takeProfitBps: [0, 100_000], takeProfitLamports: [0, 1_000_000_000_000], stopLossBps: [0, 100_000], maxHoldMs: [0, 86_400_000] }
const AUTOTRADE_NUMBER_LIMITS = { buyLamports: [0, 1_000_000_000_000], delayMs: [0, 600_000], takeProfitBps: [0, 100_000], takeProfitLamports: [0, 1_000_000_000_000], stopLossBps: [0, 100_000], maxHoldMs: [0, 86_400_000] }
const ENTRY_MODES = new Set(['pct', 'amount'])

export function gameControlIntent(store, selectedWallet, action, value, options = {}) {
  const name = String(action || '').trim()
  if (!name) throw new Error('control_intent_action_required')

  if (name.startsWith('feed.')) {
    const field = name.slice(5)
    if (FEED_BOOLEAN_FIELDS.has(field)) {
      const next = booleanValue(value)
      return persist(store.updateMints({ [field]: next }), { accepted: true, action: name, value: next })
    }
    if (FEED_NUMBER_FIELDS.has(field)) {
      const next = nonNegativeInteger(value)
      return persist(store.updateMints({ [field]: next }), { accepted: true, action: name, value: next })
    }
    throw new Error('control_intent_feed_field_not_allowed')
  }

  if (name.startsWith('trade.')) {
    const wallet = selectedWalletKey(selectedWallet)
    const field = name.slice(6)
    const owners = store.trade?.owners || {}
    const current = owners[wallet] && typeof owners[wallet] === 'object' ? owners[wallet] : { enabled: false, selected: true, entryMode: 'pct', pct: 25 }
    let next
    if (field === 'enabled') next = booleanValue(value)
    else if (field === 'entryMode') {
      next = String(value || '').trim().toLowerCase()
      if (!ENTRY_MODES.has(next)) throw new Error('control_intent_trade_entry_mode_invalid')
    } else {
      const limits = TRADE_NUMBER_LIMITS[field]
      if (!limits) throw new Error('control_intent_trade_field_not_allowed')
      next = boundedInteger(value, ...limits)
    }
    return persist(store.updateTrade({ owners: { ...owners, [wallet]: { ...current, [field]: next } } }), { accepted: true, action: name, value: next, wallet })
  }

  if (name.startsWith('autotrade.')) {
    const wallet = selectedWalletKey(selectedWallet)
    const field = name.slice(10)
    if (field === 'enabled') {
      const next = booleanValue(value)
      if (next && options.allowAutotradeEnable !== true) {
        throw new Error('control_intent_autotrade_enable_requires_host_confirmation')
      }
      return persist(store.updateAutotrade({ enabled: next, wallet }), { accepted: true, action: name, value: next, wallet })
    }
    if (field === 'singlePosition') {
      const next = booleanValue(value)
      return persist(store.updateAutotrade({ singlePosition: next, wallet }), { accepted: true, action: name, value: next, wallet })
    }
    const limits = AUTOTRADE_NUMBER_LIMITS[field]
    if (!limits) throw new Error('control_intent_autotrade_field_not_allowed')
    const next = boundedInteger(value, ...limits)
    return persist(store.updateAutotrade({ [field]: next, wallet }), { accepted: true, action: name, value: next, wallet })
  }

  if (name === 'strategy.signals') {
    const wallet = selectedWalletKey(selectedWallet)
    const owners = store.trade?.owners || {}
    const current = owners[wallet] && typeof owners[wallet] === 'object'
      ? owners[wallet]
      : { enabled: true, selected: true, pct: 25 }
    const next = booleanValue(value)
    return persist(
      store.updateTrade({ owners: { ...owners, [wallet]: { ...current, strategySignals: next } } }),
      { accepted: true, action: name, value: next },
    )
  }

  throw new Error('control_intent_action_not_allowed')
}

async function persist(request, result) {
  await request
  return result
}

function booleanValue(value) {
  if (value === true || value === 1 || value === '1') return true
  if (value === false || value === 0 || value === '0') return false
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase()
    if (text === 'true' || text === 'on' || text === 'yes') return true
    if (text === 'false' || text === 'off' || text === 'no') return false
  }
  throw new Error('control_intent_boolean_required')
}

function nonNegativeInteger(value) {
  const number = Math.floor(Number(value))
  if (!Number.isSafeInteger(number) || number < 0) throw new Error('control_intent_non_negative_integer_required')
  return number
}

function selectedWalletKey(selectedWallet) {
  const wallet = String(selectedWallet?.public_key || selectedWallet?.publicKey || '').trim()
  if (!wallet) throw new Error('control_intent_wallet_required')
  return wallet
}

function boundedInteger(value, min, max) {
  if (!Number.isSafeInteger(min) || !Number.isSafeInteger(max)) throw new Error('control_intent_field_not_allowed')
  const number = Math.floor(Number(value))
  if (!Number.isSafeInteger(number) || number < min || number > max) throw new Error('control_intent_integer_out_of_range')
  return number
}
